import { Vue, Component, Watch, Prop } from 'vue-property-decorator';
import GoogleMaterialIcon from '@/components/icons/google-material-icon/google-material-icon';
import axios from 'axios';
import { SubjectModel } from '@/models/taxonomy/subject';
import { ClassificationModel } from '@/models/taxonomy/classification';
import { taxonomyAPI } from '@/providers/apis/taxonomy/taxonomy';
import { sessionService } from '@/providers/services/auth/session';
import moment from 'moment';
import { competencyAPI } from '@/providers/apis/competency/competency';
import { DomainModel } from '@/models/proficiency/domain';
import { CompetencyModel } from '@/models/proficiency/competency';
import CompetencyChartCard from '@/components/charts/competency-chart-card/competency-chart-card';
import { DEMO_USER_ACCESS_CATEGORIES_LIST, CAP_CATEGORIES, CAP_SUBJECTS, GROUP_SORTING, DEFAULT_SUBJECT_SORTING } from '@/utils/constants';
import { sortByArray, sortByProperty } from '@/utils/utils';

@Component({
    name: 'competency-map',
    components: {
        'material-icon': GoogleMaterialIcon,
        'competency-bar-chart': CompetencyChartCard,
    },
})

export default class CompetencyMap extends Vue {

    // ---------------------------------------------------------------------
    // Properties

    private subjectList?: SubjectModel[] | any = [];

    private selectedCategory: any = {};

    private selectedSubject: any = {};

    private selectedPreview: any = {
        title: 'Published',
        isPreview: false,
    };
    private previewLst: any = [
      {
        title: 'Published',
        isPreview: false,
      },
      {
        title: 'Un-published',
        isPreview: true,
      },
    ];

    @Prop()
    private defaultCategory?: any;

    private defaultSubjectCode: string = '';

    private categories?: ClassificationModel[] | any = [];

    private groupList: any = [];

    private isLoading: boolean = false;

    private userId() {
        const session = sessionService.getSession();
        return session ? session.user_id : '';
    }

    private get isSuperUser() {
        const session = sessionService.getSession();
        return session!.permissions.indexOf('ALL_ACCESS') !== -1;
    }

    @Watch('subjectList')
    private createAvatar(subjects: any) {
        if (subjects.length) {
            subjects.map((subject: any) => {
                subject.color = '#' + (Math.random().toString(16).slice(-6));
            });
        }
    }

    // -----------------------------------------------------------------------------
    // Actions

    private onSelectPreview(value: any) {
        this.selectedPreview = value;
        if (this.selectedCategory) {
            this.onSelectCategory(this.selectedCategory);
        }
    }

    private onSelectCategory(category: any) {
        this.isLoading = true;
        this.selectedCategory = category;
        if (category) {
            this.$emit('onChangeCategory', category);
        }
        this.subjectsByCategory(category)
            .then((subjects) => {
                this.subjectList = subjects.flat(1);
                // Demo purpose for cap library
                if (this.$access.isCapLibrary) {
                    this.subjectList.map((sub: any) => {
                        sub.isDisabled = true;
                        if (CAP_SUBJECTS.indexOf(sub.id) !== -1) {
                            sub.isDisabled = false;
                        }
                        return sub;
                    });
                    this.subjectList = this.subjectList.sort((a: any, b: any) => {
                        return CAP_SUBJECTS.indexOf(a.id) - CAP_SUBJECTS.indexOf(b.id);
                    });
                    sortByProperty(this.subjectList, 'isDisabled', 'ASC');
                }

                this.groupList = this.parseSubjectGroups(this.subjectList, category.id);
                this.isLoading = false;
            });

    }

    private onSelectSubject(subject: any) {
        this.$emit('onSelectSubject', subject, this.userId());
    }

    // ------------------------------------------------------------------------------
    // Hooks

    private created() {
        this.fetchTaxonomyData();
    }

    // -----------------------------------------------------------------------------
    // Methods

    private fetchTaxonomyData() {
        this.isLoading = true;
        taxonomyAPI.fetchTaxonomyClassifications().then((classifications: any) => {
            if (this.isSuperUser) {
                classifications = DEMO_USER_ACCESS_CATEGORIES_LIST;
            }
            let defaultCategory = this.defaultCategory;
            if (!this.defaultCategory) {
                const activeCategory = classifications.find((item: any) => item.is_default);
                defaultCategory = classifications.length ? (activeCategory ? activeCategory : classifications[0]) : '';
                this.$emit('onChangeCategory', defaultCategory);
            }
            // Demo purpose for cap library
            if (this.$access.isCapLibrary) {
                classifications = classifications.map((cat: any) => {
                    cat.isDisabled = true;
                    if (CAP_CATEGORIES.indexOf(cat.id as any) !== -1) {
                        cat.isDisabled = false;
                    }
                    return cat;
                });
            }
            this.categories = classifications;
            this.subjectsByCategory(defaultCategory).then((subjects) => {
                this.subjectList = subjects.flat(1);
                // Demo purpose for cap library
                if (this.$access.isCapLibrary) {
                    this.subjectList.map((sub: any) => {
                        sub.isDisabled = true;
                        if (CAP_SUBJECTS.indexOf(sub.id) !== -1) {
                            sub.isDisabled = false;
                        }
                        return sub;
                    });
                    this.subjectList = this.subjectList.sort((a: any, b: any) => {
                        return CAP_SUBJECTS.indexOf(a.id) - CAP_SUBJECTS.indexOf(b.id);
                    });
                    sortByProperty(this.subjectList, 'isDisabled', 'ASC');
                }
                this.selectedCategory = classifications ?
                    classifications.find((category: any) => category.id === defaultCategory.id) : null;
                this.groupList = this.parseSubjectGroups(this.subjectList, this.selectedCategory.id);
                this.isLoading = false;
            });
        });
    }

    private parseSubjectGroups(subjects: any, id: string) {
        subjects.forEach((subject: any) => {
            if (subject.groupCodeLabel === null || subject.groupCodeLabel === '') {
                this.$set(subject, 'groupCodeLabel', 'Common');
            }
        });
        const excludeFilter = ['Common', 'Others'];
        let groupCodes = [...new Set(subjects.map((item: any) => item.groupCodeLabel))].filter((item) => item);
        const groupWithoutCommon = groupCodes.filter((item: any) => excludeFilter.indexOf(item) === -1);
        const groupWithCommon = groupCodes.filter((item: any) => excludeFilter.indexOf(item) !== -1);
        groupCodes = groupWithoutCommon.concat(groupWithCommon);
        sortByArray(groupCodes, GROUP_SORTING);
        const groupList: any = [];
        groupCodes.forEach((groupCode: any) => {
            const groupLevel = {
                id: groupCode,
                code: groupCode,
                title: groupCode,
                categoryId: id,
                subjects: this.createGroupSubject(subjects, groupCode),
            };
            groupList.push(groupLevel);
        });
        return groupList;
    }

    private createGroupSubject(subjects: any, groupCode: string) {
        return subjects.filter((item: any) => (item.groupCodeLabel === groupCode
            || (groupCode === 'Common' && !item.groupCodeLabel)));
    }

    private onScrollGroup(value: any) {
        const component = this;
        const activeElement = component.$el.querySelector(value) as HTMLElement;
        const containerRight = component.$el.querySelector('#competency-right') as HTMLElement;
        const headerOffset = 120;
        const elementPosition = activeElement.offsetTop;
        const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
        containerRight.scrollTop = offsetPosition;

    }

    private subjectsByCategory(categoryIds: any) {
        categoryIds = !categoryIds.included ? [categoryIds.id] : categoryIds.included;
        const promiseList = categoryIds.map((id: string) => taxonomyAPI.fetchTaxonomySubjects(id));
        return axios.all(promiseList);
    }



}
